import { useMemo, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { fetchLaundries } from '@/pages/parametrizacion/laundries/Services/laundries.services';

/**
 * Centros de lavado que el usuario tiene permitido consultar.
 *
 * Semantica (la misma que `modulosActivos`): **lista vacia = sin restriccion**.
 * Un usuario sin `centrosAsignados` ve todos los centros activos, igual que
 * antes de existir esta funcionalidad. Solo cuando el admin le asigna centros
 * explicitamente se le acota la vista.
 *
 * Es el unico punto por el que las pantallas de reportes deben cargar la lista
 * de centros: usar `cargarCentros()` en lugar de llamar a `fetchLaundries`
 * directamente, para que el filtro no se aplique de forma distinta en cada una.
 *
 * Ojo: esto acota la INTERFAZ, no la API. El backend no valida identidad, asi
 * que no es aislamiento real de datos.
 *
 * Uso:
 *   const {
 *     centrosAsignados,   // [{ id, nombre }] tal como vienen del usuario
 *     tieneRestriccion,   // boolean
 *     idsPermitidos,      // string[] (vacio si no hay restriccion)
 *     puedeVerCentro,     // (id) => boolean
 *     filtrarCentros,     // (lista) => lista acotada
 *     cargarCentros,      // () => Promise<centros activos ya filtrados>
 *     centroPorDefecto,   // id con el que arrancar los selectores
 *   } = useCentrosPermitidos();
 */
const useCentrosPermitidos = () => {
  const { user, centrosAsignados } = useAuth();

  const tieneRestriccion = centrosAsignados.length > 0;

  const idsPermitidos = useMemo(
    () => centrosAsignados.map((c) => String(c.id)),
    [centrosAsignados],
  );

  // Set para no recorrer el array en cada fila de una tabla.
  const idsSet = useMemo(() => new Set(idsPermitidos), [idsPermitidos]);

  const puedeVerCentro = useCallback(
    (id) => {
      if (!tieneRestriccion) return true;
      if (!id) return false;
      return idsSet.has(String(id));
    },
    [tieneRestriccion, idsSet],
  );

  // Los centros llegan de endpoints distintos: unas veces con `_id` (documento
  // de Mongo) y otras con `id` (objeto denormalizado). Se aceptan ambos.
  const filtrarCentros = useCallback(
    (lista) => {
      if (!Array.isArray(lista)) return [];
      if (!tieneRestriccion) return lista;
      return lista.filter((l) => puedeVerCentro(l?._id || l?.id));
    },
    [tieneRestriccion, puedeVerCentro],
  );

  const cargarCentros = useCallback(async () => {
    const resp = await fetchLaundries({ query: { estado: 'Activo' }, limit: 1000 });
    return filtrarCentros(resp?.data || []);
  }, [filtrarCentros]);

  // El centro propio del usuario puede NO estar entre los asignados (el campo
  // `lavanderia` es obligatorio al crear el usuario). En ese caso se arranca en
  // el primer centro permitido, nunca en uno prohibido.
  const centroPorDefecto = useMemo(() => {
    const propio = user?.lavanderia?._id || user?.lavanderia?.id || '';
    if (!tieneRestriccion) return propio;
    if (propio && puedeVerCentro(propio)) return propio;
    return idsPermitidos[0] || '';
  }, [user?.lavanderia, tieneRestriccion, puedeVerCentro, idsPermitidos]);

  return {
    centrosAsignados,
    tieneRestriccion,
    idsPermitidos,
    puedeVerCentro,
    filtrarCentros,
    cargarCentros,
    centroPorDefecto,
  };
};

export default useCentrosPermitidos;
